import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';

import { getRoles, updateUser } from '../services/admin_service';
import { getOrganizations } from '../services/organization_service';
import { isAdminUser, isSuperUser, isConsultantUser } from '../services/permission_service';
import { useAuth } from '../contexts/auth_context';
import { handlePermissionError, handleApiError } from '../utils/error_handling';

const useUserData = initialUser => {
	const [user, setUser] = useState(() => {
		if (initialUser) {
			return {
				id: initialUser.id,
				firstName: initialUser.first_name || '',
				lastName: initialUser.last_name || '',
				role: initialUser.role || '',
				isActive: initialUser.is_active ?? true,
				organizationId: initialUser.organization_id || null,
			};
		}
		return null;
	});

	useEffect(() => {
		if (initialUser) {
			setUser({
				id: initialUser.id,
				firstName: initialUser.first_name || '',
				lastName: initialUser.last_name || '',
				role: initialUser.role || '',
				isActive: initialUser.is_active ?? true,
				organizationId: initialUser.organization_id || null,
			});
		}
	}, [initialUser]);

	return [user, setUser];
};

const useAccessControl = () => {
	const { isAuthenticated, isLoadingAuth } = useAuth();
	const [hasAccess, setHasAccess] = useState(null);

	useEffect(() => {
		const checkAccess = async () => {
			if (!isAuthenticated || isLoadingAuth) {
				setHasAccess(null);
				return;
			}

			try {
				const isAdmin = await isAdminUser();
				const isSuper = await isSuperUser();
				setHasAccess(isAdmin || isSuper);
			} catch (error) {
				handlePermissionError(error, { context: 'user_admin_edit_view' });
				setHasAccess(false);
			}
		};
		checkAccess();
	}, [isAuthenticated, isLoadingAuth]);

	return hasAccess;
};

const useRolesData = () => {
	const [roles, setRoles] = useState([]);

	useEffect(() => {
		let mounted = true;
		(async () => {
			try {
				const resp = await getRoles();
				if (mounted && resp && Array.isArray(resp.roles)) {
					const normalized = resp.roles.map(r =>
						typeof r === 'string' ? r : r.name || r.id
					);
					setRoles(normalized);
				}
			} catch (e) {
				/* ignore */
			}
		})();
		return () => {
			mounted = false;
		};
	}, []);

	return roles;
};

const useOrganizationsData = () => {
	const [organizations, setOrganizations] = useState([]);

	useEffect(() => {
		let mounted = true;
		(async () => {
			try {
				const data = await getOrganizations();
				if (mounted && Array.isArray(data)) {
					setOrganizations(data);
				}
			} catch (e) {
				/* ignore */
			}
		})();
		return () => {
			mounted = false;
		};
	}, []);

	return organizations;
};

const formatDate = value => {
	if (!value) return '—';
	const d = new Date(value);
	if (Number.isNaN(d.getTime())) return value;
	return d.toLocaleString('en-GB', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
	});
};

const display = v => v ?? '—';

const UserAdminUserSettingsView = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const passed = location.state || {};
	const initialUser = passed.user || null;
	const { user: currentUser } = useAuth();

	const [user, setUser] = useUserData(initialUser);
	const [saving, setSaving] = useState(false);
	const [isCurrentUserSuper, setIsCurrentUserSuper] = useState(false);
	const [isCurrentUserAdmin, setIsCurrentUserAdmin] = useState(false);
	const [isCurrentUserConsultant, setIsCurrentUserConsultant] = useState(false);
	const hasAccess = useAccessControl();
	const roles = useRolesData();
	const organizations = useOrganizationsData();

	useEffect(() => {
		const checkUserRole = async () => {
			try {
				const isSuper = await isSuperUser();
				const isAdmin = await isAdminUser();
				const isConsultant = await isConsultantUser();
				setIsCurrentUserSuper(isSuper);
				setIsCurrentUserAdmin(isAdmin);
				setIsCurrentUserConsultant(isConsultant);
			} catch (error) {
				setIsCurrentUserSuper(false);
				setIsCurrentUserAdmin(false);
				setIsCurrentUserConsultant(false);
			}
		};
		checkUserRole();
	}, []);

	const isCurrentUser = user?.id === currentUser?.uid;
	const isEditingAdmin = user?.role === 'admin';
	const isEditingSuperUser = user?.role === 'superuser';
	const isEditingConsultant = user?.role === 'consultant';

	const canEditRole =
		!isCurrentUser &&
		(() => {
			if (isCurrentUserSuper) {
				// Superusers can edit anyone, including other superusers, so a
				// promotion can be undone. Never themselves: the isCurrentUser guard
				// above covers that, and the server enforces it too.
				return true;
			} else if (isCurrentUserAdmin) {
				// Admins can edit clients and consultants, but not admins or superusers
				return !isEditingAdmin && !isEditingSuperUser;
			} else if (isCurrentUserConsultant) {
				// Consultants can only edit clients
				return user?.role === 'client';
			}
			return false;
		})();

	const handleSave = async () => {
		if (!user) return;
		setSaving(true);
		try {
			await updateUser({
				userId: user.id,
				firstName: user.firstName,
				lastName: user.lastName,
				role: user.role,
				isActive: user.isActive,
				organizationId: user.role === 'client' ? user.organizationId : null,
			});
			navigate('/admin/users');
		} catch (e) {
			handleApiError(e, { context: 'save_user', userId: user?.id });
		} finally {
			setSaving(false);
		}
	};

	const renderLoading = () => <div>Loading...</div>;

	const renderNoAccess = () => <Navigate to='/' replace />;

	const renderNoUser = () => (
		<div className='p-5'>
			<h2 className='text-xl font-semibold mb-2'>User not provided</h2>
			<p className='text-sm text-gray-600'>
				Please open this page from the Users list by clicking Edit on a user.
			</p>
		</div>
	);

	const renderUserForm = () => (
		<div className='p-5'>
			<h2 className='text-xl font-semibold mb-4'>
				Edit user: {user.firstName} {user.lastName}
			</h2>

			<div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
				{/* Left column: editable fields */}
				<div className=''>
					<div className='grid gap-2 w-full'>
						<label className='block mb-3'>
							<span className='text-sm font-medium'>First name</span>
							<input
								className='mt-1 block w-full border border-[var(--app-border)] rounded-md p-2.5 bg-white text-sm'
								value={user.firstName || ''}
								onChange={e => setUser({ ...user, firstName: e.target.value })}
							/>
						</label>
						<label className='block mb-3'>
							<span className='text-sm font-medium'>Last name</span>
							<input
								className='mt-1 block w-full border border-[var(--app-border)] rounded-md p-2.5 bg-white text-sm'
								value={user.lastName || ''}
								onChange={e => setUser({ ...user, lastName: e.target.value })}
							/>
						</label>
						<label className='block mb-3'>
							<span className='text-sm font-medium'>Role</span>
							<select
								className='mt-1 block w-full border border-[var(--app-border)] rounded-md p-2.5 bg-white text-sm'
								value={user.role || ''}
								disabled={!canEditRole}
								onChange={e =>
									setUser({
										...user,
										role: e.target.value,
										organizationId:
											e.target.value === 'client'
												? user.organizationId
												: null,
									})
								}
							>
								<option value=''>-- select role --</option>
								{roles
									.filter(r => {
										if (isCurrentUserSuper) {
											// Superusers may assign any role, superuser
											// included, which is how a second superuser
											// gets made. Enforced server side too, by
											// requireAssignableRole.
											return true;
										} else if (isCurrentUserAdmin) {
											// Admins can assign client and consultant roles
											return r === 'client' || r === 'consultant';
										} else if (isCurrentUserConsultant) {
											// Consultants can only assign client role
											return r === 'client';
										}
										return false;
									})
									.map(r => (
										<option key={r} value={r}>
											{r}
										</option>
									))}
							</select>
							{!canEditRole && (
								<p className='text-xs text-gray-500 mt-1'>
									{(() => {
										if (isCurrentUser) return 'Cannot change your own role';
										if (isEditingSuperUser)
											return 'Only a superuser can modify another superuser';
										if (isCurrentUserAdmin && isEditingAdmin)
											return 'Cannot modify other admin roles';
										if (
											isCurrentUserConsultant &&
											(isEditingAdmin || isEditingConsultant)
										)
											return 'Can only modify client roles';
										return 'Insufficient permissions to change role';
									})()}
								</p>
							)}
						</label>
						{user.role === 'client' && (
							<label className='block mb-3'>
								<span className='text-sm font-medium'>Organization</span>
								<select
									className='mt-1 block w-full border border-[var(--app-border)] rounded-md p-2.5 bg-white text-sm'
									value={user.organizationId || ''}
									onChange={e =>
										setUser({ ...user, organizationId: e.target.value || null })
									}
								>
									<option value=''>-- no organization --</option>
									{organizations.map(org => (
										<option key={org.id} value={org.id}>
											{org.name}
										</option>
									))}
								</select>
							</label>
						)}
						<div className='block mb-3'>
							<span className='text-sm font-medium'>Active</span>
							<div className='mt-1 flex items-center'>
								<label className='relative inline-flex items-center cursor-pointer'>
									<input
										type='checkbox'
										className='sr-only peer'
										checked={user.isActive}
										disabled={isCurrentUser}
										onChange={e =>
											setUser({ ...user, isActive: e.target.checked })
										}
									/>
									<div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
									<span className='ml-3 text-sm font-medium text-gray-900'>
										{isCurrentUser
											? 'Cannot deactivate yourself'
											: `${user.isActive ? 'On' : 'Off'}`}
									</span>
								</label>
							</div>
						</div>

						<div className='flex gap-2'>
							<button
								type='button'
								onClick={() => navigate('/admin/users')}
								className='px-4 py-2 rounded-md bg-gray-100 hover:bg-gray-200'
							>
								Cancel
							</button>
							<button
								type='button'
								onClick={handleSave}
								disabled={saving}
								// --primary lives in global_variables.css, which is never
								// imported, so it resolved to nothing and left white text
								// on a white card. --sb-active is the app's purple accent
								// and is defined in both themes.
								className='px-4 py-2 rounded-md bg-[var(--sb-active)] text-white hover:brightness-95 disabled:opacity-50'
							>
								{saving ? 'Saving...' : 'Save'}
							</button>
						</div>
					</div>
				</div>

				{/* Right column: read-only details */}
				<div className=''>
					<div className='grid grid-cols-1 gap-3 mb-4 w-full'>
						<div className='flex flex-col'>
							<span className='text-xs text-gray-500'>ID</span>
							<span className='text-sm font-mono text-gray-700'>
								{display(user.id ?? initialUser.id)}
							</span>
						</div>
						<div className='flex flex-col'>
							<span className='text-xs text-gray-500'>Email</span>
							<span className='text-sm text-gray-700'>
								{display(initialUser.email ?? user.email)}
							</span>
						</div>
						<div className='flex flex-col'>
							<span className='text-xs text-gray-500'>Active</span>
							<span className='text-sm'>
								{user.isActive ? (
									<span className='inline-flex items-center px-2 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800'>
										Active
									</span>
								) : (
									<span className='inline-flex items-center px-2 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800'>
										Inactive
									</span>
								)}
							</span>
						</div>
						<div className='flex flex-col'>
							<span className='text-xs text-gray-500'>Last login</span>
							<span className='text-sm font-mono text-gray-700'>
								{formatDate(initialUser.last_login_at ?? user.last_login_at)}
							</span>
						</div>
						<div className='flex flex-col'>
							<span className='text-xs text-gray-500'>Created at</span>
							<span className='text-sm font-mono text-gray-700'>
								{formatDate(initialUser.created_at ?? user.created_at)}
							</span>
						</div>
						<div className='flex flex-col'>
							<span className='text-xs text-gray-500'>Updated at</span>
							<span className='text-sm font-mono text-gray-700'>
								{formatDate(initialUser.updated_at ?? user.updated_at)}
							</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	);

	if (hasAccess === null) return renderLoading();
	if (!hasAccess) return renderNoAccess();
	if (!user) return renderNoUser();

	return renderUserForm();
};

export const UserAdminUserSettings = React.memo(UserAdminUserSettingsView);
